/**
 * Axios HTTP Request Configuration
 * 
 * This file configures axios instance with:
 * - Base URL and timeout settings
 * - Request/Response interceptors
 * - Error handling
 * - Authentication token injection
 */

import axios from 'axios'
import ElementUI from 'element-ui'
import i18n, { getSavedLocale } from '@/i18n'

// Create axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:10001',
  timeout: 10000,
  withCredentials: true
})

/**
 * Request Interceptor
 * - Inject authentication token
 * - Add custom headers
 */
service.interceptors.request.use(
  config => {
    config.headers['Accept-Language'] = getSavedLocale()
    // Add auth token if exists
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    console.error('Request Error:', error)
    return Promise.reject(error)
  }
)

/**
 * Response Interceptor
 * - Handle different response structures
 * - Unified error handling
 * - Auto logout on 401
 */
service.interceptors.response.use(
  response => {
    // Handle backend response structure
    const res = response.data
    
    // If backend returns { code, message, data } structure
    if (res.code !== undefined) {
      // Success codes: 200 or 0
      if (res.code === 200 || res.code === 0) {
        return res
      } else {
        // Business error
        ElementUI.Message.error(res.message || i18n.t('common.requestFailed'))
        return Promise.reject(new Error(res.message || i18n.t('common.requestFailed')))
      }
    }
    
    // Return response data directly
    return res
  },
  error => {
    // HTTP error handling
    if (error.response) {
      const status = error.response.status
      const data = error.response.data
      
      switch (status) {
        case 401:
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          localStorage.removeItem('roles')
          if (window.location.pathname === '/login' || window.location.pathname === '/admin/login') {
            break
          }
          ElementUI.Message.error(data.message || i18n.t('common.unauthorized'))
          if (window.location.pathname.startsWith('/admin')) {
            window.location.href = '/admin/login'
          } else {
            window.location.href = '/login'
          }
          break
          
        case 403:
          {
            ElementUI.Message.warning({
              message: data.message || i18n.t('common.forbidden'),
              duration: 4000
            })
            break
          }
          
        case 404:
          // Not Found
          ElementUI.Message.error(data.message || i18n.t('common.resourceNotFound'))
          break
          
        case 500:
          // Internal Server Error
          ElementUI.Message.error(data.message || i18n.t('common.serverError'))
          break
          
        default:
          // Other errors
          ElementUI.Message.error(data.message || error.message || i18n.t('common.requestFailed'))
      }
    } else if (error.request) {
      // Network error - no response received
      ElementUI.Message.error(i18n.t('common.networkError'))
    } else {
      // Request setup error
      ElementUI.Message.error(error.message || i18n.t('common.requestFailed'))
    }
    
    return Promise.reject(error)
  }
)

export default service
